import { useQuery } from '@tanstack/react-query';
import { useLocale } from './useLocale';
import { useSelectedDateRanges } from './useSelectedDateRanges';
import { Departure, TripCodeToDepartures } from '../services/departures/departures.model';
import { getAllDeparturesForAllTrips } from 'src/services/departures/departures.requests';
import { filterDepartures } from 'src/services/departures/departures.utils';

export const useMatchingDepartures = () => {
  const { selectedDateRanges } = useSelectedDateRanges();
  const { data, isLoading, isError } = useDepartures();

  return {
    matchingDepartures: data
      ? data.reduce((acc, { tripCode, departures }) => {
          acc[tripCode] = filterDepartures(departures, selectedDateRanges).sort(
            (a, b) => a.departureDate.getTime() - b.departureDate.getTime(),
          );
          return acc;
        }, {} as TripCodeToDepartures)
      : undefined,
    isLoading,
    isError,
    allDeparturesCounts: data?.reduce(
      (acc, { tripCode, departures }) => {
        acc[tripCode] = departures.length;
        return acc;
      },
      {} as { [tripCode: string]: number },
    ),
  };
};

export const useDepartures = () => {
  const { language, currency } = useLocale();

  return useQuery({
    queryKey: ['departures', language, currency],
    queryFn: () => getAllDeparturesForAllTrips(currency, language),
    select: (data: { tripCode: string; departures: Departure[] }[]) => {
      return data.map(({ tripCode, departures }) => ({
        tripCode,
        departures: departures.map((departure) => ({
          ...departure,
          departureDate: new Date(departure.departureDate),
          returnDate: new Date(departure.returnDate),
        })),
      }));
    },
  });
};
